import { Bullseye, Button, Grid, GridItem, ToggleGroup, ToggleGroupItem } from "@patternfly/react-core";
import { Modal, ModalVariant } from "@patternfly/react-core/deprecated";
import { FormContainer } from "@rhc-shared-components/form";
import { FormRadio } from "@rhc-shared-components/form-radio-select";
import { FormTextInput } from "@rhc-shared-components/form-text-input";
import { Formik, type FormikProps } from "formik";
import { type FC, useEffect, useRef, useState } from "react";
import { ProjectTypes } from "../../../constants/project";
import { AttachExistingComponents } from "./AttachExistingComponents";
import {
	ComponentCreateValidator,
	ComponentTypes,
	CreateFormKeys,
	ProjectCreateModalKeys,
	ProjectNonContKeys,
	RHELVersions,
	RhelCreateConstants,
	StandaloneCreateInitialValues,
	StandaloneCreateModalValidator,
	StandalonePlatformCreateComponentTypes,
	useCreateAndAttachComponent,
	useCreateAndAttachNonContainerComponent,
} from "./createConstants";

interface StandaloneComponentModalProps {
	handleCreateModal: (showModal: boolean) => void;
	hideValidatedComponents?: boolean;
}

export const StandaloneComponentModal: FC<StandaloneComponentModalProps> = (props: StandaloneComponentModalProps) => {
	const ref = useRef<FormikProps<Record<string, string>>>(null);
	const attachRef = useRef<FormikProps<Record<string, string>>>(null);
	const [modalType, setModalType] = useState<"new" | "existing">("new");
	const { createNewNonContainerComponent, isCreateNonContainerComponentAPILoading, isProductDetailsLoading } =
		useCreateAndAttachNonContainerComponent(props.handleCreateModal, "new");
	const { createNewComponent, isCreateComponentAPILoading, isAttachComponentAPILoading, checkAttachmentError } =
		useCreateAndAttachComponent(props.handleCreateModal, "new");
	const handleModalTypeChange = (_selected: boolean, event: MouseEvent | React.MouseEvent | React.KeyboardEvent) => {
		const newStatus = (event.currentTarget as HTMLElement)?.id as "new" | "existing";
		setModalType(newStatus);
	};
	const getAttachableComponentTypes = () => {
		return StandalonePlatformCreateComponentTypes;
	};
	const getInitialValues = () => {
		return { ...StandaloneCreateInitialValues };
	};
	useEffect(() => {
		document.body.classList.add("stop-scrolling");
	}, []);
	return (
		<Modal
			title="Add Component"
			variant={ModalVariant.medium}
			id="add-component-modal"
			className="add-component-modal"
			appendTo={document.getElementById("modal-div-container") ?? document.body}
			isOpen
			onClose={() => {
				props.handleCreateModal(false);
				document.body.classList.remove("stop-scrolling");
			}}
			actions={[
				<Button
					key="create"
					variant="primary"
					data-analytics-category={
						modalType === "new" ? "Product Info|Add Component" : "Product Info|Attach Existing Component"
					}
					isLoading={
						isProductDetailsLoading ||
						isCreateNonContainerComponentAPILoading ||
						isAttachComponentAPILoading ||
						isCreateComponentAPILoading
					}
					isDisabled={
						isProductDetailsLoading ||
						isCreateNonContainerComponentAPILoading ||
						isAttachComponentAPILoading ||
						isCreateComponentAPILoading
					}
					onClick={() => {
						modalType === "new" ? ref?.current?.handleSubmit() : attachRef?.current?.handleSubmit();
					}}
				>
					{`${modalType === "new" ? "Create new component" : "Attach existing component"}`}
				</Button>,
				<Button
					key="cancel"
					isDisabled={
						isProductDetailsLoading ||
						isCreateNonContainerComponentAPILoading ||
						isAttachComponentAPILoading ||
						isCreateComponentAPILoading
					}
					variant="link"
					onClick={() => {
						props.handleCreateModal(false);
						document.body.classList.remove("stop-scrolling");
					}}
				>
					Cancel
				</Button>,
			]}
		>
			<Grid hasGutter>
				<GridItem>
					<Bullseye>
						<ToggleGroup>
							<ToggleGroupItem
								text="New Component"
								buttonId="new"
								isSelected={modalType === "new"}
								isDisabled={
									isProductDetailsLoading || isCreateNonContainerComponentAPILoading || isAttachComponentAPILoading
								}
								onChange={(event, selected) => handleModalTypeChange(selected, event)}
							/>
							<ToggleGroupItem
								text="Existing Component"
								buttonId="existing"
								isSelected={modalType === "existing"}
								isDisabled={
									isProductDetailsLoading || isCreateNonContainerComponentAPILoading || isAttachComponentAPILoading
								}
								onChange={(event, selected) => handleModalTypeChange(selected, event)}
							/>
						</ToggleGroup>
					</Bullseye>
				</GridItem>
				<GridItem>
					<div style={modalType === "existing" ? { display: "none" } : { display: "block" }}>
						<Formik
							innerRef={ref}
							initialValues={getInitialValues()}
							validationSchema={() => {
								return ComponentCreateValidator.concat(StandaloneCreateModalValidator);
							}}
							onSubmit={async (values) => {
								if (values?.[CreateFormKeys.componentType] === ComponentTypes.RHEL) {
									const requestData = {
										[ProjectNonContKeys.name]: values[CreateFormKeys.componentName],
										[ProjectNonContKeys.rhProductName]: RhelCreateConstants.RHEL_FULLNAME,
										[ProjectNonContKeys.rhProductVersion]: values[CreateFormKeys.productVersion],
										[ProjectNonContKeys.type]: { code: RhelCreateConstants.RHEL_CODE },
										[ProjectNonContKeys.projectStatus]: { code: "active" },
									};
									if (checkAttachmentError([requestData])) {
										return false;
									}
									await createNewNonContainerComponent.mutateAsync(requestData);
								} else if (values?.[CreateFormKeys.componentType] === ComponentTypes.OpenShiftVirtualization) {
									const requestData = {
										[ProjectCreateModalKeys.name]: values[CreateFormKeys.componentName],
										[ProjectCreateModalKeys.type]: ProjectTypes.openShiftVirtualization,
										[ProjectCreateModalKeys.projectStatus]: "active",
									};
									await createNewComponent(requestData);
								}
							}}
						>
							{({ values }) => (
								<FormContainer gridSpan={12}>
									<FormTextInput
										name={CreateFormKeys.componentName}
										label={"Component Name"}
										isRequired
										helperText={"This is for internal use only and will not be visible to customers"}
									/>
									<FormRadio
										name={CreateFormKeys.componentType}
										label={"What kind of standalone component are you creating?"}
										isRequired={true}
										options={getAttachableComponentTypes()}
										span={12}
									/>
									{values?.[CreateFormKeys.componentType] === ComponentTypes.RHEL && (
										<FormRadio
											name={CreateFormKeys.productVersion}
											label={"Red Hat Enterprise Linux Version"}
											isRequired={true}
											options={RHELVersions}
											subLabel={<div>What major version of RHEL will you be certifying your component for?</div>}
											subInfo={"This cant be changed after the project is created."}
											span={12}
										/>
									)}
								</FormContainer>
							)}
						</Formik>
					</div>
					<div style={modalType === "new" ? { display: "none" } : { display: "block" }}>
						<AttachExistingComponents
							{...(props?.hideValidatedComponents !== undefined
								? { hideValidatedComponents: props.hideValidatedComponents }
								: {})}
							handleCreateModal={props.handleCreateModal}
							ref={attachRef}
						/>
					</div>
				</GridItem>
			</Grid>
		</Modal>
	);
};
